import * as Schema from "effect/Schema";
import * as HttpServerRespondable from "effect/unstable/http/HttpServerRespondable";
import * as HttpServerResponse from "effect/unstable/http/HttpServerResponse";
import { TrimmedNonEmptyString } from "./baseSchemas.js";
import { PolicyResource, UnavailableCause } from "./actionPolicy.js";
/**
 * The server's authorization vocabulary.
 *
 * This module deliberately restates the scope and role literals rather than
 * importing them from `@dovod/config`, which owns them for the minter and the
 * verifier. `@dovod/config`'s barrel reaches `node:fs` to load agent content, and
 * `@t3tools/contracts` is bundled into the browser client — so a runtime import
 * would drag a filesystem module into the web build. The two are bound together
 * by a drift test (`authorization.test.ts`) that fails the moment they diverge,
 * which is the same shape as the parser-vocabulary drift guard.
 */
export const ServerRole = Schema.Literals(["user", "operator"]);
export const ServerScope = Schema.Literals([
    "orchestration:read",
    "orchestration:operate",
    // Retired: nothing mints it any more, but a token minted before the terminal
    // left the product still carries it and must keep decoding.
    "terminal:operate",
    "review:write",
]);
export const ServerScopes = Schema.Array(ServerScope);
export const ServerOrchestrationReadScope = "orchestration:read";
export const ServerOrchestrationOperateScope = "orchestration:operate";
export const ServerReviewWriteScope = "review:write";
/**
 * The server's auth posture. `team-server` is one team's shared workspace in
 * collaboration mode, admitting tokens that name the `web` platform; the other
 * two are the local server, admitting tokens that name `desktop`.
 */
export const ServerAuthPolicy = Schema.Literals([
    "desktop-managed-local",
    "loopback-browser",
    "team-server",
]);
/**
 * One mechanism, named once: a second entry would be a second identity
 * mechanism, and Dovod accounts are the only one.
 */
export const ServerSessionMethod = Schema.Literals(["dovod-server-token"]);
/**
 * Three states, matching the local server's authorization state machine.
 * `offline-grace` is reported to the client so it can
 * distinguish "your account is gone" from "we cannot reach Dovod right now",
 * which are the same HTTP outcome and completely different user problems.
 */
export const ServerAuthorizationState = Schema.Literals(["valid", "revoked", "offline-grace"]);
/**
 * Local state left behind by the pre-consolidation product's device pairing.
 * No sound mapping exists from a pairing subject to a Dovod user id,
 * so the rows are dropped and the user is told once, specifically, and
 * separately from everything else on disk — which survives untouched.
 */
export const LegacyAuthStateNotice = Schema.Struct({
    pairingLinks: Schema.Number,
    sessions: Schema.Number,
});
export const ServerAuthDescriptor = Schema.Struct({
    policy: ServerAuthPolicy,
    sessionMethods: Schema.Array(ServerSessionMethod),
    /** Present only on the first run after upgrading over paired local state. */
    legacyStateNotice: Schema.optionalKey(LegacyAuthStateNotice),
});
/**
 * Why the server refused an authentic token.
 *
 * - `invalid_credential`: the token does not verify here — a bad signature or
 *   audience, a malformed claim, or another team's organisation.
 * - `platform_not_allowed`: the token verifies, and the account's platforms
 *   leave out this server's (`desktop` for the local server, `web` for a team
 *   server). Signing in again mints the same answer, so the client says the
 *   account has no access here instead of offering sign-in.
 */
export const ServerTokenRejection = Schema.Literals(["invalid_credential", "platform_not_allowed"]);
export const ServerSessionState = Schema.Struct({
    authenticated: Schema.Boolean,
    auth: ServerAuthDescriptor,
    /** Present only when a presented token was refused. */
    rejection: Schema.optionalKey(ServerTokenRejection),
    /** The Dovod user id, present only when authenticated. */
    subject: Schema.optionalKey(TrimmedNonEmptyString),
    role: Schema.optionalKey(ServerRole),
    scopes: Schema.optionalKey(ServerScopes),
    expiresAt: Schema.optionalKey(Schema.DateTimeUtc),
    state: Schema.optionalKey(ServerAuthorizationState),
});
export const ServerWebSocketTicketResult = Schema.Struct({
    ticket: TrimmedNonEmptyString,
    expiresAt: Schema.DateTimeUtc,
});
export const ServerTokenRefreshInput = Schema.Struct({
    token: TrimmedNonEmptyString,
});
export const ServerTokenRefreshResult = Schema.Struct({
    expiresAt: Schema.DateTimeUtc,
    state: ServerAuthorizationState,
});
/**
 * Why an operation was refused. The reasons are not interchangeable and the
 * client renders each differently:
 *
 * - `missing_scope`: the grant is narrower than the operation.
 * - `session_revoked`: platform-api answered that the session is gone.
 * - `expired`: the token aged out and no replacement arrived.
 * - `network_unreachable`: platform-api did not answer, and the operation
 *   crosses the network. Local work is unaffected and the message says so.
 */
export const ServerAuthorizationDeniedReason = Schema.Literals([
    "missing_scope",
    "session_revoked",
    "expired",
    "network_unreachable",
    "resource_unavailable",
]);
export class EnvironmentAuthorizationError extends Schema.TaggedError()("EnvironmentAuthorizationError", {
    message: Schema.String,
    reason: ServerAuthorizationDeniedReason,
    requiredScope: Schema.optionalKey(ServerScope),
    resource: Schema.optionalKey(PolicyResource),
    cause: Schema.optionalKey(UnavailableCause),
}, { httpApiStatus: 403 }) {
    [HttpServerRespondable.symbol]() {
        return HttpServerResponse.schemaJson(EnvironmentAuthorizationError)(this, { status: 403 });
    }
}
export class ServerTokenRefreshError extends Schema.TaggedError()("ServerTokenRefreshError", {
    message: Schema.String,
}) {
}
